/** Values AAMVA uses to mean "no value". */
const SENTINELS = new Set(["NONE", "UNAVL", "UNAVAIL", "NOT PROVIDED"]);

/**
 * A payload is AAMVA if it carries the ANSI/AAMVA compliance header and at
 * least a license number. We check both because the header alone appears in
 * some non-license documents, and because Ambir's `isAamva` flag is absent
 * from their own documented response examples — we cannot rely on it.
 */
export function isAamvaPayload(raw) {
  if (!raw) return false;
  const hasHeader = /\b(ANSI|AAMVA)\s?\d/.test(raw);
  // DAQ normally starts its own line, but when it is the first element in
  // the subfile it can be glued to the DL/ID subfile designator instead
  // (`DLDAQ...`) — see the subfile-start-line handling in readElements. The
  // line may be preceded by CR/LF, space, or the AAMVA RS/US control characters
  // (`\x1e`/`\x1f`), or glued right to the subfile directory record, so both are tolerated here.
  // \x1e/\x1f are real AAMVA field separators (RS/US), not a mistake.
  // eslint-disable-next-line no-control-regex
  const hasLicenceNumber =
    /(?:^|[\r\n\x1e\x1f\s]|DL|ID)DAQ\s?\S/.test(raw) ||
    /DAQ\s?[A-Za-z0-9]/.test(raw);
  const hasOtherAamvaFields =
    // eslint-disable-next-line no-control-regex
    /(?:^|[\r\n\x1e\x1f\s])(DCS|DAC|DBB|DAG|DAI|DAJ)\s?\S/.test(raw);
  return (hasHeader && hasLicenceNumber) || (hasLicenceNumber && hasOtherAamvaFields);
}

/**
 * Split an AAMVA payload into its 3-letter element codes.
 *
 * DL/ID prefix handling is applied at exactly two POSITIONS, never by
 * sniffing line content, and never on ordinary data lines — stripping a
 * DL/ID prefix from an ordinary line would corrupt any value containing
 * those letters (e.g. a city of `IDAHO FALLS`):
 *
 * 1. The header line (the first line after the leading `@`, if present).
 *    Some payloads glue the first data element onto the end of it
 *    (`...ZC03190008DL` + `DCA` + value), so it gets a greedy match that
 *    takes the LAST `DL`/`ID` marker on the line.
 * 2. The line immediately after the header. Per the AAMVA standard the
 *    header is terminated with `\r` and the subfile designator (`DL`/`ID`)
 *    starts its own line, immediately followed by the first element
 *    (`DLDCAC` on its own line rather than glued to the header). This is
 *    an anchored, non-greedy match at the very start of the line, so it
 *    only ever fires here — an ordinary line beginning with an element
 *    code (always `D` + two more letters, never literally `DL`/`ID`) is
 *    read as-is.
 */
function readElements(raw) {
  const elements = {};

  // Real AAMVA payloads separate the header from the subfile with the
  // control characters RS (\x1e) and US (\x1f), not just CR/LF. Neither is
  // JS whitespace, so `.trim()`/`.filter(Boolean)` alone would let RS
  // survive as its own "line" and shift every subsequent positional match
  // (headerIndex / headerIndex + 1) one line late. Splitting on them here
  // keeps the line list identical for fixtures with or without RS.
  // \x1e/\x1f are real AAMVA field separators (RS/US), not a mistake.
  const lines = raw
    // eslint-disable-next-line no-control-regex
    .split(/[\r\n\x1e\x1f]+/)
    .map((line) => line.trim())
    .filter(Boolean);

  const headerIndex = lines[0] === "@" ? 1 : 0;
  const hasExplicitHeader = /\b(ANSI|AAMVA)\s?\d/.test(lines[headerIndex] || "");

  lines.forEach((line, index) => {
    let candidate;

    if (index === headerIndex && hasExplicitHeader) {
      // Greedy prefix: take everything up to the LAST DL/ID marker.
      const header = line.match(/^.*(?:DL|ID)([A-Z]{3})(.*)$/);
      if (!header) return;
      candidate = `${header[1]}${header[2]}`;
    } else if (
      index === headerIndex + 1 ||
      (index === 0 && !hasExplicitHeader)
    ) {
      // Anchored, positional-only: strip a standalone subfile designator.
      const subfile = line.match(/^(?:DL|ID)([A-Z]{3})(.*)$/);
      candidate = subfile ? `${subfile[1]}${subfile[2]}` : line;
    } else {
      candidate = line;
    }

    const match = candidate.match(/^([A-Z]{3})(.*)$/);
    if (!match) return;

    const [, code, rawValue] = match;
    const value = rawValue.trim();
    if (!value || SENTINELS.has(value.toUpperCase())) return;
    if (elements[code] === undefined) elements[code] = value;
  });

  return elements;
}

/**
 * `902100000` -> `90210`; `902101234` -> `90210-1234`; `90210` -> `90210`.
 * Any other digit count is malformed and must be dropped, never truncated —
 * a wrong ZIP silently written to a guest record is worse than a blank one.
 */
function normalizeZip(raw) {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 9) {
    const plusFour = digits.slice(5);
    return plusFour === "0000"
      ? digits.slice(0, 5)
      : `${digits.slice(0, 5)}-${plusFour}`;
  }
  if (digits.length === 5) return digits;
  return "";
}

/**
 * AAMVA dates are 8 digits, MMDDCCYY on the US licences this module supports
 * (non-US records are rejected before this runs — see the `DCG` gate in
 * `parseAamva`). Fall back to a range check (no month exceeds 12) so a US
 * card that omits `DCG` never silently misreads a year-first date.
 */
function normalizeDate(raw) {
  const digits = raw.replace(/\D/g, "");
  if (digits.length !== 8) return "";

  const firstTwo = Number(digits.slice(0, 2));
  const isYearFirst = firstTwo > 12;

  const year = isYearFirst ? digits.slice(0, 4) : digits.slice(4, 8);
  const month = isYearFirst ? digits.slice(4, 6) : digits.slice(0, 2);
  const day = isYearFirst ? digits.slice(6, 8) : digits.slice(2, 4);

  const monthNum = Number(month);
  const dayNum = Number(day);
  const yearNum = Number(year);
  if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) return "";

  // Reject impossible calendar dates (e.g. 02-31) rather than passing them
  // through — a range check alone lets `02312000` come back as `2000-02-31`.
  const isLeapYear =
    (yearNum % 4 === 0 && yearNum % 100 !== 0) || yearNum % 400 === 0;
  const daysInMonth = [
    31,
    isLeapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  if (dayNum > daysInMonth[monthNum - 1]) return "";

  return `${year}-${month}-${day}`;
}

function normalizeGender(code) {
  const u = (code || "").trim().toUpperCase();
  if (u === "1" || u === "M" || u === "MALE") return "Male";
  if (u === "2" || u === "F" || u === "FEMALE") return "Female";
  return "";
}

/**
 * `parseAamva` rejects any record whose `DCG` is present and not `USA`
 * before this runs, so only `'USA'` or an absent element (`''`) ever reach
 * here — both mean the same thing: a US licence.
 */
function normalizeCountry(code) {
  return code === "" || code === "USA" ? "United States" : "";
}

/** Legacy combined name element: `LAST,FIRST,MIDDLE` — some jurisdictions use `$` instead of `,`. */
function splitLegacyName(daa) {
  const [last = "", first = "", middle = ""] = daa
    .split(/[,$]/)
    .map((part) => part.trim());
  return { last, first, middle };
}

/**
 * Parse a `dateOfBirth` string (the `YYYY-MM-DD` shape `normalizeDate`
 * emits) as a LOCAL calendar date, never through `new Date(dateString)`.
 *
 * Per ECMAScript, a date-only ISO string (`"1985-08-31"`, no time part) is
 * parsed as UTC midnight, not local midnight. Every US front desk runs in a
 * UTC-negative zone, so once that Date is serialised back out with a
 * local-time formatter (e.g. date-fns `format()`), it renders as the
 * previous day — a scanned 1985-08-31 date of birth silently becomes
 * 1985-08-30 on screen and in whatever gets persisted. Building the Date
 * from its (year, month, day) components via the local-time constructor
 * avoids the UTC round-trip entirely.
 */
export function parseDateOfBirth(dateOfBirth) {
  if (!dateOfBirth) return null;
  const [y, m, d] = dateOfBirth.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/**
 * Parse a raw AAMVA PDF_417 payload into license data.
 * Returns null when the payload is not an AAMVA record, or when it is a
 * non-US (e.g. Canadian) licence — V1 supports US licences only, and a
 * populated non-US record would carry a fabricated country/date/ZIP.
 */
export function parseAamva(raw) {
  if (!isAamvaPayload(raw)) return null;

  const el = readElements(raw);
  if (el.DCG && el.DCG !== "USA") return null;

  const legacy = el.DAA ? splitLegacyName(el.DAA) : null;

  const lastName = el.DCS || legacy?.last || "";
  const firstName = el.DAC || el.DCT || legacy?.first || "";
  const middleName = el.DAD || legacy?.middle || "";

  return {
    firstName,
    middleName,
    lastName,
    fullName: [firstName, middleName, lastName].filter(Boolean).join(" "),
    address1: el.DAG || "",
    address2: el.DAH || "",
    city: el.DAI || "",
    state: el.DAJ || "",
    zipcode: el.DAK ? normalizeZip(el.DAK) : "",
    country: normalizeCountry(el.DCG || ""),
    licenseNumber: el.DAQ || "",
    dateOfBirth: el.DBB ? normalizeDate(el.DBB) : "",
    gender: el.DBC ? normalizeGender(el.DBC) : "",
  };
}


/**
 * Fallback parser for Ambir's pre-parsed JSON string in `barcode.parsedData`.
 */
export function parseAmbirParsedData(jsonStr) {
  if (!jsonStr) return null;
  try {
    const data = JSON.parse(jsonStr);
    const firstName = data.FirstName || data.firstName || "";
    const lastName = data.LastName || data.lastName || "";
    const middleName = data.MiddleName || data.middleName || "";
    const fullName =
      [firstName, middleName, lastName].filter(Boolean).join(" ") ||
      data.FullName ||
      "";

    return {
      firstName,
      middleName,
      lastName,
      fullName,
      address1: data.Address || data.address || data.Address1 || "",
      address2: data.Address2 || data.address2 || "",
      city: data.City || data.city || "",
      state: data.State || data.state || "",
      zipcode: data.Zip
        ? normalizeZip(data.Zip)
        : data.zip
        ? normalizeZip(data.zip)
        : "",
      country: "United States",
      licenseNumber:
        data.LIC || data.licenseNumber || data.LicenseNumber || "",
      dateOfBirth: data.DOB || data.dob || "",
      gender: normalizeGender(data.Sex || data.gender || ""),
    };
  } catch {
    return null;
  }
}
